<template>
    <span ref="reference" class="relative inline-block cursor-pointer" @mouseenter="show" @mouseleave="hide">
        <Icon name="info" class="w-4 h-4 transform origin-center stroke-current text-[rgb(var(--color-primary-500))]" />

        <Teleport to="body">
            <span
                ref="floating"
                class="p-2.5 text-gray-500 bg-white text-para-xs border rounded z-10 w-max max-w-[300px]"
                :class="[
                    'fixed',
                    isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
                ]"
            >
                <slot />
            </span>
        </Teleport>
    </span>
</template>

<script lang="ts">
import { ref, onBeforeUnmount } from 'vue';
import { computePosition, flip, shift, offset, autoUpdate } from '@floating-ui/dom';
import Icon from './Icon.vue';

export default {
    components: { Icon },
    setup() {
        const reference = ref<HTMLElement | null>(null);
        const floating = ref<HTMLElement | null>(null);
        const isVisible = ref(false);
        let cleanup: (() => void) | null = null;

        const updatePosition = () => {
            if (!reference.value || !floating.value) return;

            computePosition(reference.value, floating.value, {
                placement: 'bottom-start',
                middleware: [
                    offset(12), // Space between icon and tooltip
                    flip(), // Flip to top if no space at bottom
                    shift({ padding: 10 }), // Shift sideways to stay within container/viewport
                ],
            }).then(({ x, y }) => {
                Object.assign(floating.value!.style, {
                    left: `${x}px`,
                    top: `${y}px`,
                    position: 'absolute',
                });
            });
        };

        const show = () => {
            isVisible.value = true;
            if (reference.value && floating.value) {
                cleanup = autoUpdate(reference.value, floating.value, updatePosition);
            }
        };

        const hide = () => {
            isVisible.value = false;
            if (cleanup) {
                cleanup();
                cleanup = null;
            }
        };

        onBeforeUnmount(() => {
            if (cleanup) cleanup();
        });

        return {
            reference,
            floating,
            isVisible,
            show,
            hide,
        };
    },
};
</script>
